import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User, Shield, Clock, ShieldAlert, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { getTrustEdges } from '@/lib/trustEngine';
import { getSupabaseClient } from '@/lib/supabase/loader';

interface Delegate {
    id: string;
    name: string;
    avatar?: string;
    type: "active" | "conditional";
    authority: string;
    triggers: string;
    lastAction?: string;
}

export const CircleOfAction: React.FC = () => {
    const [selectedDelegate, setSelectedDelegate] = useState<Delegate | null>(null);
    const [delegates, setDelegates] = useState<Delegate[]>([]);

    useEffect(() => {
        async function load() {
            try {
                const { data } = await getTrustEdges();
                if (!data) return;

                // Load supabase lazily for profile lookups
                const supabase = await getSupabaseClient();

                // For each trust edge, fetch the trustee profile to display name/avatar
                const trustees = await Promise.all(
                    data.map(async (edge) => {
                        // edge.trustee_id is the user id of the delegate
                        const { data: profile } = await supabase.from('profiles').select('full_name, avatar_url').eq('id', edge.trustee_id).single();
                        return {
                            id: edge.id,
                            name: profile?.full_name || edge.trustee_id,
                            avatar: profile?.avatar_url || undefined,
                            type: edge.edge_type === 'delegate' ? (edge.is_active ? 'active' : 'conditional') : 'conditional',
                            authority: (edge.scopes || []).join(', ') || 'Limited',
                            triggers: edge.valid_until ? `Valid until ${edge.valid_until}` : (edge.triggers || 'Configured'),
                            lastAction: edge.updated_at ? `Updated ${new Date(edge.updated_at).toLocaleDateString()}` : undefined,
                        } as Delegate;
                    })
                );

                setDelegates(trustees);
            } catch (err) {
                console.error('[CircleOfAction] failed loading delegates', err);
            }
        }

        load();
    }, []);

    return (
        <div className="py-8 px-4 bg-card rounded-[1rem] border border-border overflow-hidden relative">
            <div className="mb-12 text-center">
                <h2 className="text-3xl font-serif text-white mb-2 italic">Your Circle of Action</h2>
                <p className="text-sm text-muted-foreground italic">“Who can step in if you are unavailable. You always remain in control.”</p>
            </div>

            <div className="relative h-[450px] w-full flex items-center justify-center">
                {/* Connection Lines */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none">
                    {delegates.map((delegate, index) => {
                        const angle = (index * (360 / delegates.length) - 90) * (Math.PI / 180);
                        const radius = delegate.type === "active" ? 140 : 180;
                        const x = 50 + (radius / 500) * 100 * Math.cos(angle); // Simplified local SVG coords mapping
                        // Actually let's use percentage-based lines or a fixed center
                        return null; // I'll use CSS for connections or absolute positioning
                    })}
                </svg>

                {/* User Node (Center) */}
                <div className="relative z-20">
                    <div className="absolute -inset-8 bg-primary/10 rounded-full blur-2xl animate-pulse"></div>
                    <motion.div
                        whileHover={{ scale: 1.05 }}
                        className="w-24 h-24 bg-card border-4 border-primary/50 rounded-full flex items-center justify-center shadow-2xl overflow-hidden"
                    >
                        <User className="w-12 h-12 text-primary" />
                    </motion.div>
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-primary text-secondary px-3 py-0.5 rounded-full text-[10px] font-bold uppercase">You</div>
                </div>

                {/* Delegate Nodes */}
                {delegates.map((delegate, index) => {
                    const angle = (index * (360 / delegates.length) - 90);
                    const radius = delegate.type === "active" ? 140 : 190;
                    const isSmall = delegate.type === "conditional";

                    return (
                        <motion.div
                            key={delegate.id}
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.5 + index * 0.2, type: "spring" }}
                            className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer z-30"
                            style={{
                                left: `calc(50% + ${Math.cos(angle * Math.PI / 180) * radius}px)`,
                                top: `calc(50% + ${Math.sin(angle * Math.PI / 180) * radius}px)`
                            }}
                            onClick={() => setSelectedDelegate(delegate)}
                        >
                            <div className="relative group">
                                {/* Visual Connection (Radial Ring) */}
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 border border-white/5 rounded-full -z-10 group-hover:border-primary/20 transition-colors"></div>

                                <motion.div
                                    whileHover={{ scale: 1.1 }}
                                    className={`${isSmall ? 'w-16 h-16' : 'w-20 h-20'} relative rounded-full border-2 transition-all duration-500
                    ${delegate.type === 'active' ? 'border-primary shadow-[0_0_15px_rgba(194,172,128,0.4)]' : 'border-muted-foreground/30'}`}
                                >
                                    <Avatar className="w-full h-full">
                                        <AvatarImage src={delegate.avatar} />
                                        <AvatarFallback className="bg-muted text-lg">{delegate.name.charAt(0)}</AvatarFallback>
                                    </Avatar>
                                    <div className={`absolute -top-1 -right-1 p-1 rounded-full border border-card shadow-lg
                    ${delegate.type === 'active' ? 'bg-primary text-secondary' : 'bg-muted-foreground text-white'}`}>
                                        {delegate.type === 'active' ? <Shield className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                    </div>
                                </motion.div>
                                <div className="mt-2 text-center">
                                    <p className="text-white text-xs font-medium">{delegate.name}</p>
                                    <p className="text-muted-foreground text-[10px] italic">{delegate.type}</p>
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            <AnimatePresence>
                {selectedDelegate && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 z-50 bg-background/90 backdrop-blur-md flex items-center justify-center p-6"
                    >
                        <Card className="max-w-md w-full p-6 border border-border bg-card relative">
                            <button
                                onClick={() => setSelectedDelegate(null)}
                                className="absolute top-4 right-4 text-muted-foreground hover:text-white transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>

                            <div className="flex items-center gap-6 mb-8">
                                <Avatar className="w-20 h-20 border-2 border-primary/30">
                                    <AvatarImage src={selectedDelegate.avatar} />
                                    <AvatarFallback className="text-2xl">{selectedDelegate.name.charAt(0)}</AvatarFallback>
                                </Avatar>
                                <div>
                                    <h3 className="text-2xl font-serif text-white">{selectedDelegate.name}</h3>
                                    <div className="flex items-center gap-2 mt-1">
                                        {selectedDelegate.type === 'active' ?
                                            <Badge className="bg-primary/20 text-primary border-primary/30">
                                                <Shield className="w-3 h-3 mr-1" /> Active Delegate
                                            </Badge> :
                                            <Badge variant="outline" className="text-muted-foreground border-white/10">
                                                <Clock className="w-3 h-3 mr-1" /> Conditional
                                            </Badge>
                                        }
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Scope of Authority</label>
                                    <p className="text-white flex items-center gap-2 mt-1">
                                        <ShieldCheck className="w-4 h-4 text-primary" /> {selectedDelegate.authority}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Activation Triggers</label>
                                    <p className="text-white flex items-center gap-2 mt-1">
                                        <ShieldAlert className="w-4 h-4 text-primary" /> {selectedDelegate.triggers}
                                    </p>
                                </div>
                                {selectedDelegate.lastAction && (
                                    <div>
                                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Last Action</label>
                                        <p className="text-muted-foreground text-sm italic mt-1">{selectedDelegate.lastAction}</p>
                                    </div>
                                )}
                            </div>

                            <div className="mt-8 flex gap-3">
                                <button className="flex-1 bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 py-2 rounded-lg text-sm transition-all" onClick={() => setSelectedDelegate(null)}>
                                    Close
                                </button>
                                <button className="flex-1 bg-transparent hover:bg-white/5 text-white border border-white/10 py-2 rounded-lg text-sm transition-all">
                                    Edit Scope
                                </button>
                            </div>
                        </Card>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const ShieldCheck = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" /><path d="m9 12 2 2 4-4" /></svg>
);
